import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

// Celtic symbol particles — rotating triquetra, holly, sea wave (O Murchu + Cullinane heritage)
const CELTIC_PARTICLES = [
  { src: '/triquetra.png',     x: -60, delay: 0   },
  { src: '/holly-celtic.png',  x:  20, delay: 0.1 },
  { src: '/sea-wave-celtic.png', x: -20, delay: 0.15 },
  { src: '/dara-knot.png',     x:  60, delay: 0.2 },
  { src: '/triquetra.png',     x: -40, delay: 0.08 },
] as const;

interface CompletionStepProps {
  hasFamily: boolean;
  onComplete: () => void;
}

export function CompletionStep({ hasFamily, onComplete }: CompletionStepProps) {
  const { t } = useTranslation();

  return (
    <div className="text-center relative overflow-hidden">
      {/* Celtic particle burst on mount */}
      <div className="pointer-events-none absolute inset-0">
        {CELTIC_PARTICLES.map(({ src, x, delay }, i) => (
          <motion.img
            key={i}
            src={src}
            alt=""
            className="absolute w-8 h-8"
            style={{ left: '50%', top: '30%' }}
            draggable={false}
            initial={{ opacity: 0.9, x: 0, y: 0, scale: 0.6, rotate: 0 }}
            animate={{ opacity: 0, x, y: -80, scale: 1, rotate: x > 0 ? 30 : -30 }}
            transition={{ duration: 1.4, ease: 'easeOut', delay }}
          />
        ))}
      </div>

      {/* Celebration — Cork badge + pulsing ring */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 18 }}
      >
        <div className="relative inline-flex items-center justify-center mb-4">
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-primary/30"
            style={{ width: 96, height: 96, margin: 'auto' }}
            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeOut' }}
          />
          <img src="/logo.webp" alt="Chlann" className="w-20 h-20 drop-shadow-2xl" draggable={false} />
        </div>

        <motion.h2
          className="font-display text-3xl font-bold text-ink-1 mb-2 tracking-tight"
          style={{ fontVariationSettings: '"opsz" 32' }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {t('wizard.complete.title')}
        </motion.h2>

        <motion.p
          className="text-ink-3 max-w-sm mx-auto text-sm leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
        >
          {hasFamily
            ? t('wizard.complete.subtitleWithFamily')
            : t('wizard.complete.subtitleWithoutFamily')
          }
        </motion.p>
      </motion.div>

      {/* CTA */}
      <motion.button
        onClick={onComplete}
        className="btn-primary inline-flex items-center gap-2"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {t('wizard.complete.startChatting')}
        <ArrowRight className="w-4 h-4" />
      </motion.button>
    </div>
  );
}
